﻿<template>
  <!--
    One drawing picker for every family.

    The hub declares the setting (its key, label, description and options); this
    component lays the options out as tiles of the family's *own artwork* and
    reports the chosen value. A Material Symbol per option could only say "this
    is a heat pump" for all eight machines â€” the choice is about the shape, so each
    tile shows the drawing the device's tile will show. That is why `pump`, `tank`,
    `heat_pump`, `cover`, `reading`, `appliance` and the binary sensor share this
    file instead of the seven near-identical blocks the editor carried before.
  -->
  <div>
    <label class="variant-picker__title">{{ title }}</label>
    <p v-if="description" class="variant-picker__description">{{ description }}</p>
    <div class="variant-picker__grid">
      <button
        v-for="option in options"
        :key="option.value"
        type="button"
        :title="option.label"
        :disabled="disabled"
        @click="emit('update:modelValue', option.value)"
        :class="['variant-picker__option', { 'variant-picker__option--active': modelValue === option.value }]">
        <span class="variant-picker__art">
          <span v-if="option.value === 'auto'" class="material-symbols-outlined variant-picker__auto">settings_suggest</span>
          <VariantDrawing v-else :drawing="family.drawing" :kind="option.value" :icon="option.icon" />
        </span>
        <span class="variant-picker__label">{{ option.label }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { VariantFamily } from './variants/types';
import VariantDrawing from './VariantDrawing.vue';
import { tt } from '../../utils/consoleText';

interface Props {
  /** The hub's setting definition, as the class metadata serialised it. */
  definition?: {
    label?: string;
    description?: string;
    options?: Array<{ value: unknown; label?: unknown; icon?: unknown }>;
  } | null;
  /** The family, for the bilingual labels the hub does not carry. */
  family: VariantFamily;
  /** The current `settings.<key>` value. */
  modelValue?: unknown;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), { definition: null, modelValue: undefined, disabled: false });

const emit = defineEmits<{ 'update:modelValue': [value: string] }>();

const title = computed(() => {
  const label = props.definition?.label;
  const [en, sl] = props.family.title;
  return label ? tt(String(label), sl) : tt(en, sl);
});

const description = computed(() => {
  const text = props.definition?.description;
  const [en, sl] = props.family.description;
  return text ? tt(String(text), sl) : tt(en, sl);
});

/** The hub's options, with the family's own Slovene wording where it has one. */
const options = computed<Array<{ value: string; icon: string; label: string }>>(() => {
  const declared = props.definition?.options ?? [];
  if (declared.length === 0) {
    return props.family.options.map((option) => ({
      value: option.value,
      icon: option.icon,
      label: tt(option.label[0], option.label[1]),
    }));
  }
  return declared.map((option) => {
    const value = String(option.value ?? '');
    const known = props.family.options.find((entry) => entry.value === value);
    const en = String(option.label ?? known?.label[0] ?? value);
    return {
      value,
      icon: typeof option.icon === 'string' && option.icon !== '' ? option.icon : (known?.icon ?? ''),
      label: tt(en, known?.label[1] ?? en),
    };
  });
});
</script>

<style scoped>
.variant-picker__title {
  display: block;
  margin-bottom: calc(0.25rem * var(--ui-space-factor));
  font-size: 0.875rem;
  font-weight: 500;
  color: rgb(var(--gray-700));
}

.dark .variant-picker__title {
  color: rgb(var(--gray-300));
}

.variant-picker__description {
  margin-bottom: calc(0.5rem * var(--ui-space-factor));
  font-size: 0.75rem;
  color: rgb(var(--gray-500));
}

.dark .variant-picker__description {
  color: rgb(var(--gray-400));
}

/* The tiles: wide enough for a machine, narrow enough that nine still wrap into
   a block the modal can show without scrolling. */
.variant-picker__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(5.6rem, 1fr));
  gap: calc(0.5rem * var(--ui-space-factor));
}

.variant-picker__option {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: calc(0.25rem * var(--ui-space-factor));
  padding: calc(0.4rem * var(--ui-space-factor)) calc(0.3rem * var(--ui-space-factor));
  border: 1px solid rgb(209 213 219);
  border-radius: calc(0.65rem * var(--ui-space-factor));
  background: transparent;
  color: rgb(var(--gray-700));
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease,
    color 0.15s ease;
}

.variant-picker__option:hover {
  background: rgb(243 244 246);
}

.variant-picker__option--active {
  border-color: rgb(59 130 246);
  background: rgb(239 246 255);
  color: rgb(29 78 216);
}

.variant-picker__option:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.dark .variant-picker__option {
  border-color: rgb(75 85 99);
  color: rgb(var(--gray-300));
}

.dark .variant-picker__option:hover {
  background: rgb(55 65 81);
}

.dark .variant-picker__option--active {
  border-color: rgb(59 130 246);
  background: rgb(30 58 138 / 0.3);
  color: rgb(147 197 253);
}

.variant-picker__art {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 2.6rem;
}

.variant-picker__auto {
  font-size: 1.6rem;
  opacity: 0.75;
}

.variant-picker__label {
  font-size: 0.68rem;
  line-height: 1.15;
  text-align: center;
  /* Two lines for "Expansion vessel", then an ellipsis â€” the tile keeps its size. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
